/* @flow */

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { Profile } from '../../../helpers/profiles/typesAndConstants';
import type { ModalState } from '../../../redux/modal/reducers';

import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import ParentalControlManager from '../../../helpers/parentalControl/parentalControlManager';
import { isNameAlreadyUsed } from '../../../helpers/profiles/helpers';
import ProfilesManager from '../../../helpers/profiles/profilesManager';
import { showConfirmationModal, showProfileAvatarSelectModal } from '../../../redux/modal/actions';
import { showPinModal } from '../../../redux/modal/actions';
import ButtonFX from '../../buttons/ButtonFX';
import ButtonLink from '../../buttons/ButtonLink';
import { WidthKind } from '../../buttons/types';
import Avatar from '../../profiles/Avatar';
import ProfileTypeIndicator, { ProfileType } from '../../profiles/ProfileTypeIndicator';
import { AvatarSize, EditMode } from '../../profiles/types';
import RowSettingAction from '../../settings/RowSettingAction';
import { type CONFIRMATION_DATA_MODAL_TYPE, ConfirmationModalResult } from '../confirmationModal/ConfirmationModal';
import Modal, { ModalView } from '../modal';
import { PinModalKind } from '../pinModal/PinModalConstsAndTypes';
import SharedProfileSettings from './SharedProfileSettings';
import './ProfileEditorModal.css';

export enum ProfilesEditorModalResult {
  Delete,
  Save,
}

// eslint-disable-next-line max-lines-per-function
const ProfileEditorModal = ({ modalId, profileId }: ModalState): React.Node => {
  const isInEditMode = profileId !== undefined;

  const dispatch = useDispatch();

  const modalRef = useRef<ModalView | null>(null);
  const initialAvatarId = useRef<string | null>(null);

  const [profile, setProfile] = useState<Profile>(() => (profileId !== undefined ? ProfilesManager.getProfile(profileId) : ProfilesManager.createNewProfile()));
  const [isWorking, setIsWorking] = useState(false);

  useEffect(() => {
    if (initialAvatarId.current === null) {
      // Save initial avatar Id to allow selecting it back in avatar selection modal
      initialAvatarId.current = profile.avatarId;
    }
  }, [profile.avatarId]);

  const setModalRef = useCallback((instance: ModalView | null) => {
    modalRef.current = instance;
  }, []);

  /*
   * Parameter avatarId could have 3 values:
   *  - string:    Id of the avatar
   *  - null:      no avatar
   *  - undefined: user closed the avatar selector via the top-right close button (i.e. cancel)
   */
  const updateAvatar = useCallback(async (avatarId: ?string) => {
    if (avatarId === undefined) {
      // Canceled by user
      return;
    }

    setProfile((currentProfile) => {
      return { ...currentProfile, avatarId };
    });
  }, []);

  const close = useCallback((result: ProfilesEditorModalResult) => {
    modalRef.current?.close(result);
  }, []);

  const saveProfile = useCallback(async (): Promise<boolean> => {
    // Adjust name before saving
    const newProfile: Profile = { ...profile, name: profile.name.trim() };
    setProfile(newProfile);

    if (newProfile.name === '') {
      Messenger.emit(MessengerEvents.NOTIFY_WARNING, Localizer.localize('profiles.errors.empty_name'));
      return false;
    }

    // Check if name already exists for another profile
    const profiles = await ProfilesManager.getProfiles();
    if (isNameAlreadyUsed(profiles, newProfile.id, newProfile.name)) {
      return false;
    }

    if (profileId) {
      // Update existing profile
      return await ProfilesManager.updateProfile(newProfile);
    } else {
      // Create new profile

      // Set default restrictions, if provided by DMS
      if (newProfile.isKid && ProfilesManager.defaultKidsRestrictions) {
        newProfile.restrictions = ProfilesManager.defaultKidsRestrictions;
      } else if (ProfilesManager.defaultRestrictions) {
        newProfile.restrictions = ProfilesManager.defaultRestrictions;
      }

      return await ProfilesManager.addProfile(newProfile);
    }
  }, [profile, profileId]);

  const handleSaveOnClick = useCallback(async () => {
    setIsWorking(true);
    const success = await saveProfile();
    setIsWorking(false);

    if (success) {
      // Only close editor if profile was successfully saved
      close(ProfilesEditorModalResult.Save);
    }
  }, [close, saveProfile]);

  const profileDeleteConfirmationClosedCallback = useCallback(
    async (result: ConfirmationModalResult) => {
      if (result !== ConfirmationModalResult.Button2) {
        // Delete canceled
        return;
      }

      if (profileId !== undefined) {
        setIsWorking(true);
        await ProfilesManager.deleteProfile(profileId);
        setIsWorking(false);
      }

      close(ProfilesEditorModalResult.Delete);
    },
    [profileId],
  );

  const deleteProfile = useCallback(() => {
    const data: CONFIRMATION_DATA_MODAL_TYPE = {
      button1Title: Localizer.localize('common.actions.cancel'),
      button2Title: Localizer.localize('common.actions.delete'),
      header: Localizer.localize('profiles.profile_editor.delete_profile.title'),
      question: Localizer.localize('profiles.profile_editor.delete_profile.question'),
    };

    const confirmationModalId = crypto.randomUUID();
    Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${confirmationModalId}`, profileDeleteConfirmationClosedCallback);
    dispatch(showConfirmationModal(data, Localizer.localize('accessibility.modal.confirmation_delete_profile', { name: profile.name }), confirmationModalId));
  }, [profile, profileDeleteConfirmationClosedCallback]);

  const handleDeleteOnClick = useCallback(() => {
    if (ProfilesManager.isProfileManagementLocked) {
      // Profile deletion is restricted: Master PIN is required
      const modalId = crypto.randomUUID();
      Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${modalId}`, (isvalidPin: boolean) => {
        if (isvalidPin) {
          deleteProfile();
        }
      });

      dispatch(showPinModal({ kind: PinModalKind.CheckMaster }, modalId));
    } else {
      // Profile deletion is accessible to anyone
      deleteProfile();
    }
  }, [deleteProfile]);

  const handleAvatarEditOnClick = useCallback(() => {
    const id = crypto.randomUUID();
    Messenger.once(`${MessengerEvents.MODAL_CLOSED}-${id}`, updateAvatar);
    dispatch(showProfileAvatarSelectModal(profile?.avatarId ?? null, initialAvatarId.current, id));
  }, [profile, updateAvatar]);

  const handleNameOnChange = useCallback(async (event: SyntheticInputEvent<HTMLInputElement>) => {
    const name = event.target.value.trimStart();

    setProfile((currentProfile) => {
      return { ...currentProfile, name };
    });
  }, []);

  const handleProfileOnChange = useCallback((newProfile: Profile) => {
    setProfile(newProfile);
  }, []);

  return (
    <Modal
      ariaLabel={Localizer.localize(isInEditMode ? 'accessibility.modal.profile_editor_edition' : 'accessibility.modal.profile_editor_creation', { name: profile.name })}
      canCloseOnOverlayClick={false}
      className='profileEditor'
      header={Localizer.localize(isInEditMode ? 'profiles.profile_editor.title_edition' : 'profiles.profile_editor.title_creation')}
      innerRef={setModalRef}
      modalId={modalId}
    >
      <div className='profileEditorContainer'>
        <div className='profileEditorAvatarAndName'>
          <Avatar
            aria-label={Localizer.localize('accessibility.profiles.select_avatar')}
            avatarId={profile.avatarId}
            editMode={EditMode.ShowOnHover}
            name={profile.name}
            onClick={handleAvatarEditOnClick}
            profileId={profile.id}
            size={AvatarSize.Big}
          />
          <ButtonLink className='selectAvatarLink' onClick={handleAvatarEditOnClick}>
            {Localizer.localize('profiles.profile_editor.select_avatar_btn')}
          </ButtonLink>
          <label className='screenReaderOnly' htmlFor='profile-name'>
            {Localizer.localize('accessibility.modal.enter_profile_name')}
          </label>
          <input
            className='profileEditorName'
            id='profile-name'
            maxLength='20'
            onChange={handleNameOnChange}
            placeholder={Localizer.localize('profiles.profile_editor.name_hint')}
            type='text'
            value={profile.name}
          />
          {isInEditMode && profile.isKid ? <ProfileTypeIndicator outlined profileType={ProfileType.Kid} /> : null}
        </div>
        {ParentalControlManager.isFeatureDisabled ? null : <SharedProfileSettings canChangeProfileType={!isInEditMode} onProfileChange={handleProfileOnChange} profile={profile} />}
        {isInEditMode ? (
          <RowSettingAction aria-label={Localizer.localize('accessibility.profiles.delete_profile', { name: profile.name })} isDisabled={isWorking || profile.isDefault} onClick={handleDeleteOnClick}>
            <div className='pcLabel'>{Localizer.localize('common.actions.delete')}</div>
            {profile.isDefault ? <div className='profileEditorHint'>{Localizer.localize('profiles.profile_editor.delete_profile.disabled_for_default')}</div> : null}
          </RowSettingAction>
        ) : null}
        <div className='profileEditorFooter'>
          <ButtonFX isDisabled={isWorking} onClick={handleSaveOnClick} widthKind={WidthKind.Large}>
            {Localizer.localize('profiles.profile_editor.validate')}
          </ButtonFX>
        </div>
      </div>
    </Modal>
  );
};

export default ProfileEditorModal;
